<template lang="pug">
  .product-invoice
    y-dialog(fill-height fluid v-model="showModal" scrollable :fullscreen="$vuetify.breakpoint.xsOnly" transition="fade-transition" persistent max-width="600px")
      v-card
        v-card-title.px-3.white
          v-row(no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="showModal = !showModal" height)
                v-icon(color="basic800" :size="24") mdi-close
            v-col.ml-3
              div.d-flex
                p.mb-0.h6.basic800--text {{ lead.paid ? 'Receipt' : 'Invoice' }} &#35;{{ lead.rid }}
                v-spacer
                v-chip.text-uppercase.my-auto.button-AA-tiny(small outlined label :color="lead.status | leadStatusColor" :style="{ 'background-color': chipBackgroundColor(lead.status) + '!important' }" ) {{ lead.status | leadStatusDisplay }}
                template(v-if="lead.paid")
                  v-chip.my-auto.button-AA-tiny.ml-2(small color="primary300" @click="showPrintDialog = true") 
                    v-icon.mr-1(small) mdi-cloud-print-outline
                    | {{ $t('print') }}
                template(v-else-if="Auth('root', 'payment_mark_as_paid')")
                  v-chip.my-auto.button-AA-tiny.ml-2(small color="primary300" @click="showMarkPaidDialog = !showMarkPaidDialog") {{ $t('mark_as_paid') }}
      
        v-layout.my-6(v-if="isFetching")
          v-row.fill-height.ma-0(align='center' justify='center')
            v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
        v-card-text.pa-0.text-center(v-else)
          v-container.pt-4
            div
              eva-icon(name="checkmark-circle-2-outline" :fill="lead.paid ? $vuetify.theme.themes[$theme].success500 : $vuetify.theme.themes[$theme].basic600T48" height="60" width="60")
              p.s1.mb-0.mt-5(:class="lead.paid ? 'primary500--text' : 'danger500--text'") {{ lead.paid ? $t('amount_paid') : $t('balance_due') }}
              template(v-if="lead.paid")
                div.h4.primary500--text.text-center {{ totalPaid | displayPrice }}
                div.s1.primary400--text.mt-1 {{ lead.paid_at | convertServerDate }}
                v-btn.button-Aa-small.custom-transform-class.text-none.mt-2(v-if="hasTackingCode" color="textColorAccent" text  @click="showParcelHistoryDialog = true" small)
                  v-icon.mr-2.c1(color="textColorAccent" small) mdi-map-marker-radius-outline
                  | {{ $t('track_your_parcel') }}
              template(v-else)
                div.h4.primary500--text.text-center {{ amountDue | displayPrice }}
                template(v-if="installmentEnabled")
                  div.d-flex.justify-center
                    y-checkbox.mt-1(center v-model="lead.installment" dense hide-details @change="updateInstallment" color="black")
                      template(v-slot:label)
                        div.s1 {{ $t('allow_partial_payment') }}
                  div.d-flex.justify-center(v-if="lead.installment")
                    v-btn.button-AA-tiny.mt-2(color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}" @click="showSetAmountDialog = true")
                      v-icon.mr-1(small) mdi-plus
                      | {{ $t('add_amount_received') }}

            v-divider.basic300.my-6
            .company-details.text-left.mt-4(v-if="company")
              div.d-flex
                div.s1.primary500--text {{ company.name }}
                v-btn.button-AA-tiny.px-2.ml-3(v-if="!company.address" color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}" @click="showUpdateCompanyDetails = !showUpdateCompanyDetails")
                  v-icon.mr-1(x-small) mdi-pencil
                  | {{ $t('edit') }}
              div.p2.primary500--text {{ company.phone }}
              div.p2.primary500--text(v-if="company.address")
                div {{ company.address.address_1 }},
                div(v-if="company.address.address_2 ") {{ company.address.address_2 }},
                template(v-if="company.address.area")
                  div {{ company.address.postcode }} {{ company.address.area }},
                  div {{ company.address.suburb }}, {{ company.address.city }},
                div(v-else) {{ company.address.postcode }} {{ company.address.city }},
                div {{ company.address.state }}, {{ company.address.country }}.

            .client-details.text-left.mt-8
              div.p1.primary500--text {{ $t('bill_to') }}:
              div.s1.primary500--text {{ lead.name }}
              div.p2.primary500--text(v-if="lead.email") {{ lead.email }}
              div.p2.primary500--text {{ lead.phone | phoneFormat }}

              div.mt-5(v-if="lead.shipping_address && !lead.pickup")
                .ship-to-others(v-if="lead.shipping_address.phone && !lead.phone.endsWith(lead.shipping_address.phone)")
                  div.p1.primary500--text {{ $t('ship_to') }}:
                  div.s1.primary500--text(v-if="lead.shipping_address.name") {{ lead.shipping_address.name }}
                  div.p2.primary500--text(v-if="lead.shipping_address.phone") {{ lead.shipping_address.phone | phoneFormat }}
                .ship-to-self(v-else)
                  div.p1.primary500--text {{ $t('shipping_address') }}:
                div.p2.primary500--text {{ lead.shipping_address.address_1 }},
                div.p2.primary500--text(v-if="lead.shipping_address.address_2") {{ lead.shipping_address.address_2 }},
                template(v-if="lead.shipping_address.area")
                  div.p2.primary500--text {{ lead.shipping_address.postcode }} {{ lead.shipping_address.area }},
                  div.p2.primary500--text {{ lead.shipping_address.suburb }}, {{ lead.shipping_address.city }},
                div.p2.primary500--text(v-else) {{ lead.shipping_address.postcode }} {{ lead.shipping_address.city }},
                div.p2.primary500--text {{ lead.shipping_address.state }}, {{ lead.shipping_address.country }}.

              div.pt-4(v-if="!lead.paid")
                v-btn.button-AA-tiny.white--text(v-if="lead.shipping_address" color="info500" outlined x-small depressed height="24" @click="showUpdateCustomerAddress = true")
                  v-icon(left x-small) mdi-pencil
                  | {{ $t('edit_address') }}
                v-btn.button-AA-tiny.white--text(v-if="!lead.shipping_address" color="info500" x-small depressed height="24" @click="showUpdateCustomerAddress = true")
                  v-icon(left small) mdi-plus
                  | {{ $t('add_address') }}
                div.mt-2.c1.primary400--text *{{ $t('required_for_order_delivery') }}

            v-divider.my-4.basic300

            v-simple-table(dense)
              thead
                tr
                  th.pa-0(style="border: none")
                    div.s1.primary400--text.text-start.mr-1 {{ $t('description') }}
                  th.pa-0(style="border: none")
                    div.s1.primary400--text.text-end.mx-1 {{ $t('price') }}
                  th.pa-0(style="border: none")
                    div.s1.primary400--text.text-end.ml-1 {{ $t('amount') }}
              tbody
                template(v-for="p in invoice.products")
                  tr(:key="p.id")
                    td.text-start.pa-0(style="border: none; vertical-align:top;")
                      div.my-1
                        div.p2.primary500--text.mr-1 {{ p.quantity }} x {{ getName(p) }} {{ p.addons.length > 0 ? ' set' : '' }}
                        div.c1.danger500--text(v-if="p.product.inventory_enabled && p.product.inventory_qty < p.quantity && !lead.paid") {{ p.product.inventory_qty > 0 ? `Only ${p.product.inventory_qty} left in stock` : 'Sold out' }}
                        div.ml-3
                          //- VARIANT NAME
                          template(v-if="sameVariantParentName(p)")
                            div.c1.basic600--text.mt-1.mr-1 {{ p.product.name }}
                          //- ADDON LIST
                          template(v-if="p.addons.length > 0")
                            div.c1.basic600--text.mt-1.mr-1 {{ p.name }}
                            div(v-for="(a, idx) in p.addons" :key="'addon-' + idx")
                              div.c1.basic600--text.mr-1 + {{ a.name }}{{ a.quantity_per_set > 1 ? ' x ' + a.quantity_per_set : '' }}
                              div.c1.danger500--text(v-if="a.inventory_enabled && a.inventory_qty < a.quantity && !lead.paid") {{ a.inventory_qty > 0 ? $t('only_count_left_in_stock', { qty: a.inventory_qty }) : $t('sold_out') }}
                          //- BUNDLE LIST
                          template(v-if="p.bundles.length > 0")
                            div.mt-1(v-for="(b, idx) in p.bundles" :key="'bundle-' + idx")
                              div.c1.basic600--text.mr-1 {{ idx + 1 }}. {{ b.name }}
                              //- BUNDLE PRODUCT NOTES
                              div(v-if="b.product_notes_data && b.product_notes_data.length > 0")
                                div(v-for="note in b.product_notes_data" :key="note.id")
                                  div.c1.primary400--text.ml-1 - 
                                    strong {{ note.name }}: 
                                    | {{ note.data }}
                              div.c1.danger500--text(v-if="b.product.inventory_enabled && b.product.inventory_qty < b.quantity && !lead.paid") {{ b.product.inventory_qty > 0 ? $t('only_count_left_in_stock', { qty: a.inventory_qty }) : $t('sold_out') }}
                          //- PRODUCT NOTES
                          div.mt-1(v-if="p.product_notes_data && p.product_notes_data.length > 0")
                            div(v-for="note in p.product_notes_data" :key="note.id")
                              div.c1.primary400--text
                                strong {{ note.name }}: 
                                | {{ note.data }}
                        
                    td.text-end.pa-0(style="border: none; min-width: 70px; vertical-align:top;")
                      div.my-1
                        div.p2.primary500--text.mx-1 {{ p.price_per_set | displayPrice }}
                        template(v-if="p.addons.length > 0")
                          div.c1.basic600--text.mx-1.mt-1 {{ p.price | displayPrice }}
                          template(v-for="a in p.addons")
                            div.c1.basic600--text.mx-1 {{ a.price * a.quantity_per_set | displayPrice }}
                    td.text-end.pa-0(style="border: none; min-width: 70px; vertical-align:top;")
                      div.my-1.p2.primary500--text.ml-1 {{ p.price_per_set * p.quantity | displayPrice }}

            div.my-6.dotted
            div
              div.d-flex.mt-1
                div.p1.primary400--text {{ $t('subtotal') }}:
                v-spacer
                div.p1.primary400--text {{ subtotalPrice | displayPrice }}
              div.d-flex.mt-1(v-if="discountPrice")
                div.p1.primary400--text {{ $t('discount') }}:
                v-spacer
                div.p1.primary400--text {{ discountPrice | displayPrice }}
              div.d-flex.mt-1(v-if="promoDiscountPrice")
                div.p1.primary400--text {{ $t('promo_discount') }}:
                v-spacer
                div.p1.primary400--text {{ promoDiscountPrice | displayPrice }}
              div.d-flex.mt-1
                div.p1.primary400--text {{ $t('tax') }}:
                v-spacer
                div.p1.primary400--text {{ taxAmount }}%
              div.d-flex.align-center.mt-1(v-if="shippingFee !== null && !lead.pickup")
                div.p1.primary400--text {{ $t('delivery_charge') }}:
                v-spacer
                div.p1.primary400--text(v-if="shippingFee === 0")
                  v-chip.px-1(color="success500" small label outlined)
                    v-icon(small).pr-1 mdi-truck-fast
                    | {{ $t('free_shipping') }}
                div.p1.primary400--text(v-else) {{ shippingFee | displayPrice }}
              div.d-flex.mt-1(v-if="serviceFee")
                div.p1.primary400--text {{ $t('service_fee') }}:
                v-spacer
                div.p1.primary400--text {{ serviceFee | displayPrice }}
              div.d-flex.mt-1
                div.h6.primary--text {{ $t('total') }}:
                v-spacer
                div.h6.primary--text {{ totalPrice | displayPrice }}
              div.d-flex.mt-1
                div.p1.primary400--text {{ $t('amount_paid') }}:
                v-spacer
                div.p1.primary400--text {{ totalPaid | displayPrice }}

              .installment(v-if="lead.installment && !lead.paid")
                div.d-flex.py-2.ml-n2.mr-n2.mt-1(:style="`background-color: ${$vuetify.theme.themes[$theme].dangerT16}; border-radius: 4px`")
                  div.s1.primary--text.ml-2 {{ $t('balance_due') }}:
                  v-spacer
                  div.s1.primary--text.mr-2 {{ amountDue | displayPrice }}
                div.d-flex.mt-2
                  div.c1.primary400--text {{ $t('please_complete_payment_before') }}: {{ invoice.installment_deadline | humanizeDate}}

              div.d-flex.mt-2(v-if="isEditable || invoice.coupons.length > 0")
                div.p1.primary400--text {{ $t('coupon_code') }}:
                v-spacer
                div
                  template(v-if="invoice.coupons.length > 0")
                    div.d-flex(@click="isEditable? showApplyCouponDialog = true : ''")
                      v-spacer
                      div.body-2.text-right.success500--text {{ invoice.coupons[0].code }}
                      template(v-if="isEditable" style="cursor:pointer")
                        v-icon.ml-1(small color="danger500") mdi-delete-outline
                    div.c1.text-right.danger500--text(v-if="!invoice.coupons[0].valid") *{{ $t('min_order') }}: {{invoice.coupons[0].minimum_order_amount | displayPrice }}
                  div.body-2.my-auto.text-right.info500--text(v-else style="width:120px; cursor:pointer" @click="showApplyCouponDialog = true") {{ $t('select_coupon') }}
              
              template(v-if="lead.cod")
                div.d-flex.mt-1
                  div.p1.primary400--text {{ $t('payment_type') }}:
                  v-spacer
                  div.p1.primary400--text {{ $t('cash_on_delivery') }}
              template(v-if="lead.pay_at_shop")
                div.d-flex.mt-1
                  div.p1.primary400--text {{ $t('payment_type') }}:
                  v-spacer
                  div.p1.primary400--text {{ $t('pay_at_shop') }}

              div.pt-6(align="center" v-if="lead.pickup")
                v-card.pa-4(outlined max-width="500px" :style="`border: 2px solid ${$vuetify.theme.themes[$theme].primary300} !important;`")
                  div.s1.success500--text.d-flex.justify-center.align-center
                    eva-icon.mr-1.mt-1(name="pin-outline" width="16px" height="16px" :fill="$vuetify.theme.themes[$theme].success500")
                    div {{ $t('self_pickup') }}
                  div.mt-2.p2.primary400--text(style="word-break: break-word;")
                    template(v-if="lead.shipping")
                      linkify-text.body-2(v-if="(typeof lead.shipping.tracking.pickup_address) === 'object'" :text="getStringFromAddress(lead.shipping.tracking.pickup_address)")
                      linkify-text.body-2(v-else :text="lead.shipping.tracking.pickup_address")
                
                div.mt-12.mb-6
                  div.f1.primary400--text {{ $t('powered_by_yezza') }}
                  div.f2.primary400--text(v-if="lead.paid") {{ $t('this_computer_generated_receipt') }}
                  div.f2.primary400--text(v-else) {{ $t('this_computer_generated_invoice') }}

        v-card-actions
          v-spacer
          v-btn(color='white', text @click='showModal = false')
            .button-Aa-medium.primary400--text {{ $t('cancel') }}
          v-btn.mr-2(v-if="!lead.paid" color='textColorAccent' depressed height=32 @click='clickSendInvoice')
            .button-AA-medium.basic100--text {{ $t('send_to_customer') }}

    .modals
      parcel-history-dialog(v-if="showParcelHistoryDialog" :open.sync="showParcelHistoryDialog" :lead="lead" :shipping="lead.shipping")
      update-company-details(v-if="showUpdateCompanyDetails" :open.sync="showUpdateCompanyDetails" :company.sync="company")
      add-customer-address(v-if="showUpdateCustomerAddress" :open.sync="showUpdateCustomerAddress" :parentLead.sync="lead")
      apply-coupon-dialog(v-if="showApplyCouponDialog" :open.sync="showApplyCouponDialog" :invoice="invoice" @applied="getInvoice")
      y-dialog(v-model='showMarkPaidDialog', max-width='320')
        v-card
          v-card-title.h6.basic800--text {{ $t('update_payment_status') }}
          v-card-text.p2
            div.danger500--text(v-if="outOfStock") {{ $t('update_payment_no_stock') }}
            div.primary500--text(v-else) {{ $t('update_payment_confirmation') }}
          v-card-actions
            v-spacer
            v-btn(color='white', text @click='showMarkPaidDialog = false')
              .button-Aa-small.primary400--text {{ $t('cancel') }}
            v-btn.mr-2(color='textColorAccent' depressed height=32 @click='proceedMarkAsPaid' :loading="isLoading" :disabled="outOfStock")
              .button-AA-small.basic100--text {{ $t('update') }}

      y-dialog(v-if="showSetAmountDialog" v-model='showSetAmountDialog', max-width='320')
        v-card
          v-card-title.h6.basic800--text {{ $t('set_amount') }}
          v-divider.basic300
          v-card-text.pt-4.pb-0
            v-form(ref="form")
              p.mb-1.label.basic600--text.text-uppercase {{ $t('price_amount.label') }}
              v-text-field.mt-2(v-model='paymentAmount' :placeholder="$t('price_amount.placeholder')" background-color="basic200" height="40" outlined dense type="number" :rules="priceRules" :prefix="$currency.symbol")

          v-card-actions
            v-spacer
            v-btn(color='white', text @click='showSetAmountDialog = false; paymentAmount = null')
              .button-Aa-small.primary400--text {{ $t('cancel') }}
            v-btn.mr-2(color='textColorAccent' depressed height=32 @click='clickPayPartialAmount()' :loading="isLoading" dark)
              .button-AA-small.basic100--text {{ $t('add_this_amount') }}

      y-dialog(v-model='showPrintDialog', max-width='290')
        v-card.pa-4
          div.mb-2.h6.basic800--text.text-center {{ $t('select_paper_size') }}
          v-row(no-gutters)
            v-col.pr-2(align="center")
              v-btn(color='textColorAccent' block depressed height=32 @click="printReceipt('small')")
                .button-AA-small.basic100--text {{ $t('small_receipt') }}
            v-col.pl-2(align="center")
              v-btn(color='textColorAccent' block depressed height=32 @click="printReceipt('large')")
                .button-AA-small.basic100--text {{ $t('a4_paper') }}

</template>

<script>
import { mapState, mapActions } from 'vuex'
import UpdateCompanyDetails from '@/components/dialogs/settings/UpdateCompanyDetails.vue'
import AddCustomerAddress from '@/components/dialogs/database/AddCustomerAddress.vue'
import ApplyCouponDialog from '@/views/invoice/components/ApplyCouponDialog'
import ParcelHistoryDialog from '@/views/invoice/components/ParcelHistoryDialog'
import LinkifyText from '@/components/LinkifyText.vue'

export default {
  name: 'ProductInvoiceDialog',
  props: ['open', 'parentLead','company'],
  components: { UpdateCompanyDetails, AddCustomerAddress, ApplyCouponDialog, ParcelHistoryDialog, LinkifyText },
  data: () => ({
    isFetching: true,
    isLoading: false,
    invoice: null,
    paymentAmount: null,
    showMarkPaidDialog: false,
    showSetAmountDialog: false,
    showParcelHistoryDialog: false,
    showUpdateCompanyDetails: false,
    showUpdateCustomerAddress: false,
    showApplyCouponDialog: false,
    showPrintDialog: false,
    // company: null,
    rules: {
      not_empty: [val => (val || '').length > 0 || 'This field is required']
    }
  }),
  computed: {
    ...mapState('user', ['member']),
    lead: {
      get() {
        return this.parentLead
      },
      set(newValue) {
        this.$emit('update:parentLead', newValue)
      }
    },
    isEditable () {
      if (this.lead.paid) {
        return false
      }
      if (this.lead.cod) {
        return false
      }
      if (this.lead.installment && this.lead.status == 'partial_payment') {
        return false
      }
      return true
    },
    priceRules () {
      return [
        v => !!v || 'Amount is required',
        v => v != 0|| 'Amount is required',
        v => v >= this.minPaymentAmount/this.$currency.multiplier || `Amount should not be below ${this.$currency.symbol} ${(parseFloat(this.minPaymentAmount/this.$currency.multiplier)).toFixed(this.$currency.exponent)}`,
        v => v <= this.amountDue/this.$currency.multiplier || `Amount should not be above ${this.$currency.symbol} ${(parseFloat(this.amountDue/this.$currency.multiplier)).toFixed(this.$currency.exponent)}`,
      ]
    },
    outOfStock() {
      if (this.lead.cod) return false
      if (this.lead.pay_at_shop) return false
      if (!this.invoice) return false
      for (let i = 0; i < this.invoice.products.length; i++) {
        let p = this.invoice.products[i]
        if (p.product.inventory_enabled && p.product.inventory_qty < p.quantity) {
          return true
        }
      }
      return false
    },
    shippingFee () {
      if (!this.invoice) return 0
      return this.invoice.shipping_fee
    },
    discountPrice () {
      if (!this.invoice) return 0
      return this.invoice.total_discount
    },
    promoDiscountPrice() {
      if (!this.invoice) return 0
      return this.invoice.total_promo_discount
    },
    subtotalPrice () {
      if (!this.invoice) return 0
      return this.invoice.subtotal_amount
    },
     serviceFee() {
      if (!this.invoice) return 0
      return this.invoice.service_fee
    },
    totalPrice () {
      if (!this.invoice) return 0
      return this.invoice.total_amount
    },
    totalPaid () {
      if (!this.invoice) return 0
      return this.invoice.total_paid
    },
    amountDue () {
      if (!this.invoice) return 0
      return this.invoice.total_amount - this.invoice.total_paid
    },
    taxAmount () {
      if (!this.invoice) return 0
      return this.invoice.company.tax_rate
    },
    minPaymentAmount () {
      if (!this.invoice.minimum_payment) return 0
      if (this.invoice.minimum_payment > this.amountDue) {
        return this.amountDue
      }
      return this.invoice.minimum_payment
    },
    installmentEnabled () {
      if (this.lead.form.invoice_settings && this.lead.form.invoice_settings.extra_settings) {
        return this.lead.form.invoice_settings.extra_settings.installment_enabled
      }
      return false
    },
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    hasTackingCode () {
      if (this.lead.shipping && (this.lead.shipping.tracking_id || this.lead.shipping.shipping_method === 'easyparcel')) {
        return true
      }
      return false
    }
  },
  mounted () {
    this.$axios.get(`/leads/${this.lead.pid}/`)
      .then((response) => {
        this.lead = response.data
        this.getInvoice()
      })
      .catch(() => {})
    // this.fetchCompanyDetails()
    if (this.hasTackingCode) {
      this.updateTracking()
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    getStringFromAddress (address) {
      return `${address.address_1},${address.address_2 ? ` ${address.address_2},` : ''} ${address.postcode},${address.area ? ` ${address.area}, ${address.suburb},` : ''} ${address.city}, ${address.state}, ${address.country}`
    },
    getInvoice () {
      this.isFetching = true
      this.$axios.get(`/invoices/${this.lead.pid}/`)
        .then((response) => {
          this.isFetching = false
          this.invoice = response.data
          if (this.invoice.paid) {
            this.lead.paid = true
            if (this.lead.status !== 'received' && this.lead.status !== 'shipped' && this.lead.status !== 'cancelled') {
              this.lead.status = 'processed'
            }
          } else if (this.invoice.total_paid > 0) {
            this.lead.status = 'partial_payment'
          }
        })
        .catch(() => {})
    },
    getName (product) {
      if (product.product.variant_for) {
        if (this.sameVariantParentName(product)) {
          return product.name.replace(`(${product.product.name})`, '')
        }
      }
      return product.name
    },
    sameVariantParentName (product) {
      return product.name.includes(`(${product.product.name})`)
    },
    clickCopyBankNo (bankNo) {
      this.showMessage(['success', `Copied: ${bankNo}`])
    },
    // fetchCompanyDetails () {
    //   this.$axios.get(`/companies/${this.lead.company}/`).
    //     then((response) => {
    //       this.company = response.data
    //     })
    //     .catch(() => {})
    // },
    getShareLinkURL () {
      let path = 'invoices'
      if (this.lead.paid) {
        path = 'receipts'
      }
      return `${this.$subdomain}.yezza.io/${path}/${this.lead.pid}`
    },
    clickSendInvoice () {
       
      window.open (`https://api.whatsapp.com/send?phone=${this.lead.phone}&text=${this.getShareLinkURL()}`)
    },
    proceedMarkAsPaid () {
      this.paymentAmount = this.amountDue/this.$currency.multiplier
      this.proceedTransactions()
    },
    clickPayPartialAmount () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.proceedTransactions()
    },
    proceedTransactions() {
      const amountInCent = (parseFloat(this.paymentAmount)).toFixed(this.$currency.exponent).replace('.', '')
      const params = {
        amount: amountInCent,
        gateway: 'direct',
        lead: this.lead.id,
        paid: true
      }
      if (amountInCent==0) {
        this.showMessage(['error', this.$t('error_message.invalid_amount')])
        return
      }
      this.isLoading = true
      this.axios.post(`/transactions/`, params)
        .then(() => {
          this.showSetAmountDialog = false
          this.showMarkPaidDialog = false
          this.isFetching = true
          this.getInvoice()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateInstallment () {
      const params = {
        installment: this.lead.installment,
      }
      this.axios.patch(`/leads/${this.lead.pid}/`, params)
        .then(resp => {
          this.lead = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    printReceipt (size) {
      if (size === "large") {
        let printUrl = `https://pdf.yezza.io/?url=http://localhost:8080/print-invoices/${this.lead.pid}&type=pdf&filename=receipt.pdf&waitUntil=networkidle0&printBackground=true&format=A4`
        if (this.$subdomain) {
          printUrl = `https://pdf.yezza.io/?url=${this.$subdomain}.yezza.io/print-invoices/${this.lead.pid}&type=pdf&filename=receipt.pdf&waitUntil=networkidle0&printBackground=true&format=A4`
        }
        if (this.$isMobile) {
          window.open(printUrl)
        } else {
          location.href = printUrl
        }
      } else if (size === "small") {
        let printUrl = `https://pdf.yezza.io/?url=http://localhost:8080/print-invoices-small/${this.lead.pid}&type=pdf&filename=receipt.pdf&waitUntil=networkidle0&printBackground=true&width=65mm`
        if (this.$subdomain) {
          printUrl = `https://pdf.yezza.io/?url=${this.$subdomain}.yezza.io/print-invoices-small/${this.lead.pid}&type=pdf&filename=receipt.pdf&waitUntil=networkidle0&printBackground=true&width=65mm`
        }
        if (this.$isMobile) {
          window.open(printUrl)
        } else {
          location.href = printUrl
        }
      }
    },
    updateTracking () {
      this.axios.post(`/shipping/shippings/${this.lead.shipping.id}/update-tracking`, {}).catch(() => {})
    },
    chipBackgroundColor (status) {
      if (status === 'new') {
        return this.$vuetify.theme.themes[this.$theme].infoT08
      } else if (status === 'follow_up') {
        return this.$vuetify.theme.themes[this.$theme].warningT08
      } else if (status === 'partial_payment') {
        return this.$vuetify.theme.themes[this.$theme].basic600T08
      } else if (status === 'reject') {
        return this.$vuetify.theme.themes[this.$theme].dangerT08
      } else if (status === 'processed') {
        return this.$vuetify.theme.themes[this.$theme].successT08
      } else if (status === 'shipped') {
        return this.$vuetify.theme.themes[this.$theme].warningT08
      } else if (status === 'received') {
        return this.$vuetify.theme.themes[this.$theme].basic600T08
      } else if (status === 'cancelled') {
        return this.$vuetify.theme.themes[this.$theme].dangerT08
      } else if (status === 'error') {
        return this.$vuetify.theme.themes[this.$theme].dangerT08
      } else if (status === '') {
        return this.$vuetify.theme.themes[this.$theme].basic600T08
      }
      return this.$vuetify.theme.themes[this.$theme].basic600T08
    }
  }
}
</script>

<style scoped>
.dotted {
  border-top: 2px dashed var(--v-primary400);
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<i18n>
{
  "en": {
    "form_name": {
      "label": "Form Name",
      "placeholder": "Write form name here"
    },
    "customer_info": "What Info Do You Collect",
    "customer_expalain": "Check or add fileds that you want to appear in your customer form.",
    "customer_name": {
      "label": "Name",
      "placeholder": "Customer name"
    },
    "customer_phone": {
      "label": "Phone",
      "placeholder": "Customer phone no"
    },
    "customer_email": {
      "label": "Email",
      "placeholder": "Customer email"
    },
    "price_amount": {
      "label": "Amount",
      "placeholder": "0"
    },
    "add_fields": "+ Collect Extra Info",
    "add_products": "+ Add Products",
    "send_to_customer": "Send to Customer",
    "allow_partial_payment": "Allow partial payment",
    "add_amount_received": "Add amount received",
    "required_for_order_delivery": "Required for order with delivery",
    "min_order": "Min order",
    "update_payment_status": "Update payment status",
    "update_payment_no_stock": "Not enough stock to proceed.",
    "update_payment_confirmation": "Are you sure you want to update this invoice status? This action is irreversible.",
    "add_this_amount": "Add this amount"
  },
  "id": {
    "form_name": {
      "label": "Nama Form",
      "placeholder": "Tulis nama form di sini"
    },
    "customer_info": "Info apa ingin dikumpulkan",
    "customer_expalain": "Pilih atau tambah info apa yang kamu ingin keluarkan di form pelanggan.",
    "customer_name": {
      "label": "Nama",
      "placeholder": "Nama"
    },
    "customer_phone": {
      "label": "Telepon",
      "placeholder": "Nomor HP pelanggan"
    },
    "customer_email": {
      "label": "Email",
      "placeholder": "Email Pelanggan"
    },
    "price_amount": {
      "label": "Jumlah",
      "placeholder": "0"
    },
    "add_fields": "+ Info tambahan",
    "add_products": "+ Tambah produk",
    "send_to_customer": "Hantar ke pelanggan",
    "allow_partial_payment": "Izinkan pembayaran sebagian",
    "add_amount_received": "Catat Jumlah yang telah diterima",
    "required_for_order_delivery": "Dibutuhkan untuk pengiriman pesanan",
    "min_order": "Order Minimal",
    "update_payment_status": "Perbarui Status pembayaran",
    "update_payment_no_stock": "Stok tidak cukup untuk melanjutkan.",
    "update_payment_confirmation": "Yakin ingin memperbarui status faktur ini? Tindakan ini tidak dapat diubah.",
    "add_this_amount": "Tambahkan jumlah ini"
  }
}
</i18n>
